// src/components/RoofPlanViewer.jsx
import React, { useEffect, useState } from "react";
import { Typography, Box, Grid } from "@mui/material";
import axios from "axios";
import DocumentButton from "./DocumentButton";

const API_BASE_URL = "https://formbuilderapi.getforsite.com";

/**
 * Mirrors SiteInformationViewer but for "roof plans".
 * - Reads any preloaded data from responses.roofInformation
 * - Fetches /api/roof-information/:siteId if not preloaded
 * - Writes back into responses via handleChange("siteInformation", ...)
 */
export default function RoofPlanViewer({
  siteId,
  responses,
  handleChange,
  isEditable,
}) {
  const preloaded = Array.isArray(responses.siteInformation)
    ? responses.siteInformation
    : [];

  const [documents, setDocuments] = useState(preloaded);

  useEffect(() => {
    if (preloaded.length) return; // already have data

    const fetchDocuments = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/roof-information/${siteId}`,
        );
        setDocuments(data.documents || []);
      } catch (error) {
        console.error("Error fetching roof information:", error);
        alert("An error occurred while fetching roof plan documents.");
      }
    };

    if (siteId) fetchDocuments();
  }, [siteId, preloaded.length]);

  // Mirror back into responseData if changed
  useEffect(() => {
    const current = responses.siteInformation || [];
    const same =
      current.length === documents.length &&
      current.every(
        (d, i) =>
          d.id === documents[i]?.id &&
          d.title === documents[i]?.title &&
          d.uploaded_url === documents[i]?.uploaded_url,
      );

    if (!same) handleChange("siteInformation", documents);
  }, [documents, responses.siteInformation, handleChange]);

  if (!siteId) return null;

  return (
    <Box marginY={2} width="100%">
      <Typography variant="h6" gutterBottom>
        Roof Plan Documents
      </Typography>
      {documents.length === 0 ? (
        <Typography>No roof plans available.</Typography>
      ) : (
        <Grid container spacing={2}>
          {documents.map((doc) => (
            <Grid item key={doc.id} xs={12} sm={4}>
              <DocumentButton
                doc={doc}
                onClick={() => window.open(doc.uploaded_url, "_blank")}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
